<template>
    <pc-table
        id="PaymentHistoryTable"
        ref="paymentsTable"
        show-items-per-page-selector
        v-bind:items="payments"
        v-bind:columns="columns"
        v-bind:initial-sort="initialSort"
        v-bind:empty-text="noPaymentHistory"
        v-bind:hide-actions="!isHistoryExpandable"
        v-bind:card-property-cutoff="4"
        v-bind:hide-search="hideSearch"
        card-show-empty-placeholder
        key-prop="ChargeId">
        <template v-slot:PayerEmail="charge">
            <div>
                {{ payerEmailValue(charge) }}
            </div>
        </template>
        <template v-slot:PayerName="charge">
            <div>
                {{ payerNameValue(charge) }}
            </div>
        </template>
        <template v-slot:Status="charge">
            <div class="text-nowrap">
                <span v-bind:class="getStatusDisplayClass(charge.Status)">
                    {{ charge.StatusDisplayName }}
                </span>
            </div>
        </template>
        <template v-slot:UserLogin="charge">
            <div>
                {{ getUserDisplay(charge.UserLogin) }}
            </div>
        </template>
        <template v-slot:actionsRow="charge">
            <payment-history-action-row
                v-bind:charge="charge"
                v-bind:current-payment-provider="currentPaymentProvider"
                v-bind:adapter-options="adapterOptionsForProvider(charge.PaymentProviderId)"
                v-bind:is-read-only="isReadOnly"
                v-bind:on-edit-status="onEditChargeStatus"
                v-on:view-payment-history="viewPaymentHistory" />
        </template>
    </pc-table>
</template>

<script>
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/paymentHistoryTable.i18n.js";
    import paymentHistoryDetail from "./paymentHistoryDetail.vue";
    import paymentHistoryActionRow from "./paymentHistoryActionRow.vue";
    import { statusDisplayClass } from "./ePayments";
    import { formatBasicUserInfoForDisplay } from "Util/clientUser";

    const CANCEL_RESULT = "CANCEL";

    export default {
        components: { paymentHistoryActionRow },
        props: {
            payments: { type: [Array, Function], required: true },
            currentPaymentProvider: { type: Number, required: true },
            adapterOptions: { type: Object, required: true },
            hideSearch: { type: Boolean, required: false, default: false },
            getChargeDetails: {
                type: Function,
                required: false,
                default: (charge) => {
                    return charge;
                }
            },
            showProjectNumbers: { type: Boolean, required: false, default: false },
            showPaymentBreakdowns: { type: Boolean, required: false, default: false },
            showReceiptIds: { type: Boolean, required: false, default: true },
            isReadOnly: { type: Boolean, required: false, default: true },
            onEditChargeStatus: { type: Function, required: false, default: () => {} }
        },
        data() {
            return {
                initialSort: [[5, "desc"]]
            };
        },
        computed: {
            ...mapI18n(
                {
                    ...KEYS
                },
                NAMESPACE
            ),
            // NOTE: These columns must correspond to the columnNamnes in GetAllChargesByProgramSql
            // GetAllChargesByProgramSql controls sorting for this table, and sorts based on the index of the column
            // being ordered.
            columns() {
                const self = this;
                return [
                    {
                        header: this.projectNumberColumn,
                        property: "ProjectNumber",
                        orderable: this.showProjectNumbers,
                        searchable: this.showProjectNumbers,
                        visible: this.showProjectNumbers
                    },
                    {
                        header: this.payerEmailColumn,
                        property: "PayerEmail",
                        orderable: this.usePayerEmail,
                        searchable: this.usePayerEmail,
                        visible: this.usePayerEmail
                    },
                    {
                        header: this.payerNameColumn,
                        property: "PayerName",
                        orderable: this.usePayerName,
                        searchable: this.usePayerName,
                        visible: this.usePayerName
                    },
                    {
                        header: "Charge Amount",
                        property: "BaseChargeAmount",
                        orderable: this.showPaymentBreakdowns,
                        searchable: this.showPaymentBreakdowns,
                        visible: this.showPaymentBreakdowns
                    },
                    {
                        header: "Transaction Fee",
                        property: "SurchargeAmount",
                        orderable: this.showPaymentBreakdowns,
                        searchable: this.showPaymentBreakdowns,
                        visible: this.showPaymentBreakdowns
                    },
                    {
                        header: this.amountColumnHeader,
                        property: "Amount",
                        orderable: true,
                        searchable: true,
                        visible: true
                    },
                    {
                        header: this.statusColumn,
                        property: "Status",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue(item) {
                            return item.StatusDisplayName;
                        }
                    },
                    {
                        header: this.dateColumn,
                        property: "DateTime",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue: "TimeTicks",
                        isCardHeader: true
                    },
                    {
                        header: this.changedByColumn,
                        property: "UserLogin",
                        orderable: true,
                        searchable: true,
                        visible: true,
                        sortValue(item) {
                            return self.getUserDisplay(item.UserLogin);
                        }
                    },
                    {
                        header: this.formColumn,
                        property: "Form",
                        orderable: true,
                        searchable: true,
                        visible: true
                    },
                    {
                        header: this.recieptNumberColumn,
                        property: "ExternalReceiptId",
                        orderable: true,
                        searchable: true,
                        visible: this.showReceiptIds
                    }
                ];
            },
            amountColumnHeader() {
                return this.showPaymentBreakdowns ? "Total Amount" : this.amountColumn;
            },
            usePayerEmail() {
                return (
                    this.adapterOptions &&
                    this.currentPaymentProvider &&
                    this.adapterOptionsForProvider(this.currentPaymentProvider).UsePayerEmailInHistory
                );
            },
            usePayerName() {
                return (
                    this.adapterOptions &&
                    this.currentPaymentProvider &&
                    this.adapterOptionsForProvider(this.currentPaymentProvider).UsePayerNameInHistory
                );
            },
            isHistoryExpandable() {
                return (
                    this.adapterOptions &&
                    this.currentPaymentProvider &&
                    this.adapterOptionsForProvider(this.currentPaymentProvider).IsHistoryExpandable
                );
            }
        },
        methods: {
            adapterOptionsForProvider(providerId) {
                return this.adapterOptions[providerId];
            },
            statusNameOverrides(paymentProviderId) {
                return this.adapterOptions && this.adapterOptionsForProvider(paymentProviderId).StatusNameOverrides;
            },
            getStatusDisplayClass(status) {
                return statusDisplayClass(status);
            },
            payerEmailValue(charge) {
                return charge.PayerEmail || "";
            },
            payerNameValue(charge) {
                return charge.PayerName || "";
            },
            getUserDisplay(user) {
                return formatBasicUserInfoForDisplay(user, null, true);
            },
            async viewPaymentHistory(charge) {
                let chargeDetails = charge;
                chargeDetails = await this.getChargeDetails(charge);
                this.$app.showDialog({
                    title: this.paymentHistoryDialogTitle,
                    large: true,
                    customComponent: paymentHistoryDetail,
                    customComponentOptions: {
                        currentPaymentProvider: charge.PaymentProviderId,
                        charge: chargeDetails,
                        adapterOptions: this.adapterOptionsForProvider(charge.PaymentProviderId)
                    },
                    buttons: [
                        {
                            id: "PaymentStatusHistoryClose",
                            text: this.closeButtonText,
                            result: CANCEL_RESULT
                        }
                    ]
                });
            },
            refresh() {
                this.$refs.paymentsTable.refresh();
            },
            getRequestFromCurrentTableView() {
                const dataTableRequest = {
                    start: -1,
                    length: -1,
                    search: {
                        value: this.$refs.paymentsTable.searchTerm,
                        regex: false
                    },
                    order: this.$refs.paymentsTable.order,
                    columns: this.$refs.paymentsTable.columns
                };

                return dataTableRequest;
            }
        }
    };
</script>
